import React, { useMemo } from 'react'

import Form from 'react-bootstrap/Form'
import ListGroup from 'react-bootstrap/ListGroup'
import Stack from 'react-bootstrap/Stack'

import { useInventory } from '../../../../InventoryProvider/InventoryProvider'
import { ListItem } from './ListItem'
import { Item } from '../Item'

function sectionLabel(section) {
  return section?.sectionNumber || section?.sectionName || section?.sectionId
}

export default function InventoryMenu({
  canEdit,
  isEventOnsale,
  holds,
  kills,
  offers,
  sold,
  resale,
  allowResales,
  handleRemove,
  handleAdd,
  getObjectTotalSeatCount,
  isDroppableInSelected,
  sections,
  hiddenSectionIds = [],
  onToggleSectionHidden,
}) {
  const { setHolds, setKills, holdLetterMap } = useInventory()
  const resaleItem = resale?.[Object.keys(resale || {})[0]] || {
    id: 'resale',
    name: 'Resale',
    seats: [],
    gaSeats: {},
  }
  const resaleCount = getObjectTotalSeatCount(resaleItem)
  const showResale = allowResales && resaleCount > 0
  const hiddenIds = useMemo(
    () => new Set((hiddenSectionIds || []).map((id) => String(id))),
    [hiddenSectionIds]
  )
  const sectionList = useMemo(() => {
    return Object.values(sections || {})
      .filter((section) => section?.sectionId != null)
      .sort((a, b) =>
        String(sectionLabel(a)).localeCompare(
          String(sectionLabel(b)),
          undefined,
          {
            numeric: true,
          }
        )
      )
  }, [sections])
  return (
    <div className="list-container inventory-container">
      <ListGroup as="ul" variant="flush">
        {sectionList.length > 0 && (
          <ListGroup.Item as="li" className="section-visibility">
            <div className="heading--flex-space-between mb-1">
              <span className="fw-semi-bold">Sections</span>
            </div>
            <small className="text-muted d-block mb-2">
              Hidden sections are grey on the public map and are not for sale.
            </small>
            <Stack as="ul" className="section-visibility__list">
              {sectionList.map((section) => {
                const id = String(section.sectionId)
                const isHidden = hiddenIds.has(id)
                const label = sectionLabel(section)
                return (
                  <li key={id} className="section-visibility__row">
                    <span className="section-visibility__name">
                      Section {label}
                    </span>
                    <Form.Check
                      type="switch"
                      id={`hide-section-${id}`}
                      className="mb-0"
                      label={isHidden ? 'Hidden' : 'Hide'}
                      aria-label={
                        isHidden
                          ? `Show section ${label}`
                          : `Hide section ${label}`
                      }
                      checked={isHidden}
                      disabled={!canEdit}
                      onChange={() =>
                        onToggleSectionHidden?.(section.sectionId)
                      }
                    />
                  </li>
                )
              })}
            </Stack>
          </ListGroup.Item>
        )}
        <ListGroup.Item as="li">
          <ListItem
            title="Offers"
            data={offers}
            getObjectTotalSeatCount={getObjectTotalSeatCount}
            isDroppableInSelected={isDroppableInSelected}
            canEdit={canEdit}
          />
        </ListGroup.Item>
        <ListGroup.Item as="li">
          <ListItem
            title="Holds"
            data={holds}
            handleRemove={handleRemove}
            handleAdd={handleAdd}
            getObjectTotalSeatCount={getObjectTotalSeatCount}
            isDroppableInSelected={isDroppableInSelected}
            canEdit={canEdit}
            setHolds={setHolds}
            setKills={setKills}
            holdLetterMap={holdLetterMap}
          />
        </ListGroup.Item>
        <ListGroup.Item as="li">
          <ListItem
            title="Kills"
            data={kills}
            handleRemove={handleRemove}
            handleAdd={handleAdd}
            getObjectTotalSeatCount={getObjectTotalSeatCount}
            isDroppableInSelected={isDroppableInSelected}
            canEdit={canEdit}
            setHolds={setHolds}
            setKills={setKills}
          />
        </ListGroup.Item>
        {(isEventOnsale ||
          (sold &&
            Object.keys(sold).length > 0 &&
            (sold[Object.keys(sold)[0]]?.seats?.length > 0 ||
              Object.keys(sold[Object.keys(sold)[0]]?.gaSeats || {}).length >
                0))) && (
          <ListGroup.Item as="li">
            <div className="heading--flex-space-between mb-2">
              <span className="fw-semi-bold">Sold</span>
            </div>
            <Stack as="ul">
              <li>
                <Item
                  el={sold[Object.keys(sold)[0]]}
                  getObjectTotalSeatCount={getObjectTotalSeatCount}
                />
              </li>
            </Stack>
          </ListGroup.Item>
        )}
        {showResale && (
          <ListGroup.Item as="li">
            <div className="heading--flex-space-between mb-2">
              <span className="fw-semi-bold">Resale</span>
            </div>
            <Stack as="ul">
              <li>
                <Item
                  el={resaleItem}
                  getObjectTotalSeatCount={getObjectTotalSeatCount}
                />
              </li>
            </Stack>
          </ListGroup.Item>
        )}
      </ListGroup>
    </div>
  )
}
